'use client';

import useShowMoreLinks from '@alltrails/layout/hooks/useShowMoreLinks';
import type { SeoLinkItem } from '@alltrails/types/SeoLinkItem';
import LinksColumn from '../LinksColumn';
import { TagsSection, typeToLabel } from '../Tags/TagsComponents';
import type { Section } from '../Tags/types';
import styles from './MinimizedSeoLinksGrid.module.scss';

const MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT = 3;

type MinimizedSeoLinksGridSharedProps = {
  linkItems: SeoLinkItem[];
  firstColumnSize: number;
  maxLayoutLinks: number;
  allowCollapsedColumnSplit?: boolean;
  columnContainerClassName: string;
  isBot?: boolean;
};

type MinimizedSeoLinksGridSeoLinksProps = MinimizedSeoLinksGridSharedProps & {
  variant: 'seolinks';
  seoTestAddTrailsToLocationLinks?: boolean;
  seoTestAddHikingTrailsToLocationLinks?: boolean;
};

type MinimizedSeoLinksGridTagsProps = MinimizedSeoLinksGridSharedProps & {
  variant: 'tags';
  section: Section;
};

type MinimizedSeoLinksGridProps = MinimizedSeoLinksGridSeoLinksProps | MinimizedSeoLinksGridTagsProps;

export default function MinimizedSeoLinksGrid(props: MinimizedSeoLinksGridProps) {
  const { linkItems, firstColumnSize, maxLayoutLinks, allowCollapsedColumnSplit = true, variant, isBot = false } = props;

  const { visibleLinkItems, showMoreSlot, isExpanded } = useShowMoreLinks({
    linkItems,
    collapsedLimit: MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT,
    isBot
  });

  const splitCollapsedWithToggle = showMoreSlot && !isExpanded && allowCollapsedColumnSplit;

  let visibleFirstBatch = visibleLinkItems.slice(0, firstColumnSize);
  let visibleSecondBatch = visibleLinkItems.slice(firstColumnSize, maxLayoutLinks);

  if (splitCollapsedWithToggle) {
    const collapsedRow = visibleFirstBatch;
    visibleSecondBatch = collapsedRow.slice(2);
    visibleFirstBatch = collapsedRow.slice(0, 2);
  }

  const showMoreToggleSlot = showMoreSlot ? <div className={styles.showMoreToggle}>{showMoreSlot}</div> : null;

  if (variant === 'seolinks') {
    const showToggleInFirstColumn =
      visibleSecondBatch.length === 0 && visibleFirstBatch.length > MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT && showMoreToggleSlot;

    return (
      <>
        <div className={props.columnContainerClassName}>
          <LinksColumn
            links={visibleFirstBatch}
            seoTestAddTrailsToLocationLinks={props.seoTestAddTrailsToLocationLinks}
            seoTestAddHikingTrailsToLocationLinks={props.seoTestAddHikingTrailsToLocationLinks}
          />
          {showToggleInFirstColumn}
        </div>
        {!!visibleSecondBatch.length && (
          <div className={props.columnContainerClassName}>
            <LinksColumn
              links={visibleSecondBatch}
              seoTestAddTrailsToLocationLinks={props.seoTestAddTrailsToLocationLinks}
              seoTestAddHikingTrailsToLocationLinks={props.seoTestAddHikingTrailsToLocationLinks}
            />
            {showMoreToggleSlot}
          </div>
        )}
      </>
    );
  }

  const renderSecondColumn = visibleSecondBatch.length >= 1 && allowCollapsedColumnSplit;
  const showToggleAfterSecondColumn = splitCollapsedWithToggle || (showMoreSlot && isExpanded && renderSecondColumn);
  return (
    <>
      <div className={props.columnContainerClassName}>
        <TagsSection heading={typeToLabel[props.section.type]}>
          <LinksColumn links={visibleFirstBatch} />
          {!showToggleAfterSecondColumn && showMoreToggleSlot}
        </TagsSection>
      </div>
      {renderSecondColumn && (
        <div className={props.columnContainerClassName}>
          <TagsSection heading={typeToLabel[props.section.type]} shouldHideTitle>
            <LinksColumn links={visibleSecondBatch} />
            {showToggleAfterSecondColumn && showMoreToggleSlot}
          </TagsSection>
        </div>
      )}
    </>
  );
}
